import { IMeetingDetailsDTO } from './meetings.dao';
import { IEventRequestParams, ISortFieldsRequestParams, ISortRequestParams } from './shared.dto';

abstract class IArtifactsDAO {
    public abstract requestMeetingsArtifactsInBatch(params: IEventRequestParams & IRequestArtifactsPayload): Promise<void>;
    public abstract getAllArtifacts(params: IEventRequestParams): Promise<IInitialArtifactsListDTO>;
    public abstract downloadSingleArtifact(
        params: IArtifactsParams,
        assetName: string
    ): Promise<void>;
    public abstract deleteSingleArtifact(params: IArtifactsParams): Promise<void>;
    public abstract updateSingleArtifact(params: IEventRequestParams): Promise<IArtifactDTO>;
    public abstract createArtifactLink(params: IArtifactsParams): Promise<IArtifactPublicLinkDTO>;
    public abstract deleteArtifactLink(params: IArtifactsParams): Promise<void>;
    public abstract publishPassportReplay(params: IArtifactsParams): Promise<void>;
}

interface IArtifactForDownload {
    downloadUrl: string;
}

enum ArtifactStatus {
    READY = 'READY',
    PENDING = 'PENDING',
    FAILED = 'FAILED',
    NOT_AVAILABLE = 'NOT_AVAILABLE',
    REVIEW_REQUIRED = 'REVIEW_REQUIRED',
    PUBLISHED = 'PUBLISHED',
}

enum RecordingType {
    DEFAULT = 'Default',
    SHARED_SCREEN_WITH_SPEAKER_VIEW_CC = 'shared_screen_with_speaker_view(CC)',
    SHARED_SCREEN_WITH_SPEAKER_VIEW = 'shared_screen_with_speaker_view',
    SHARED_SCREEN_WITH_GALLERY_VIEW = 'shared_screen_with_gallery_view',
    SPEAKER = 'speaker_view',
    GALLERY_VIEW = 'gallery_view',
    SHARED_SCREEN = 'shared_screen',
}

enum ReplaysOptions {
    REVIEW_REQUIRED = 'REVIEW_REQUIRED',
    PUBLISH_AUTOMATICALLY = 'PUBLISH_AUTOMATICALLY',
}

type TReplaysOptionsForBatchRequest = keyof typeof ReplaysOptions;

enum ArtifactType {
    TRANSCRIPT = 'TRANSCRIPT',
    MP3 = 'MP3',
    MP4_720P = 'MP4_720P',
    MP4 = 'MP4',
    PASSPORT_REPLAY = 'PASSPORT_REPLAY',
}

enum ArtifactsTypeForBatchRequest {
    TRANSCRIPT = 'TRANSCRIPT',
    MP3 = 'MP3',
    MP4 = 'MP4',
}

enum ResolutionMp4 {
    DEFAULT = 'Default',
    MP4_360 = '360p',
    MP4_720 = '720p',
    MP4_1080 = '1080p'
}

interface IArtifactDTO {
    assetMetadataId: number;
    assetType: ArtifactType;
    note: string;
    assetStatus: ArtifactStatus;
    assetName: string;
    publicDownloadLink: string | null;
    resolution?: string;
    recordingType?: WebinarRecordingType;
}

interface IMeetingArtifactDTO extends Pick<
    IMeetingDetailsDTO,
    'topic' | 'meetingId' | 'type' | 'startDateTime' | 'endDateTime'
    > {
    presenters: string[];
    artifacts: IArtifactDTO[];
}

interface IRequestMeetingArtifactDTO extends IMeetingDetailsDTO {
    resolution?: ResolutionMp4;
    recordingType?: RecordingType;
}

interface IMeetingsArtifactsListDTO {
    content: IMeetingArtifactDTO[];
    totalElements: number;
}

interface IInitialArtifactsListDTO {
    content: IInitialArtifactDTO[];
    totalElements: number;
}

interface IInitialArtifactDTO {
    startDateTime: Date;
    endDateTime: Date;
    meetingId: number;
    topic: string;
    presenters: string[];
    type: string;
    assetType: ArtifactType;
    status: ArtifactStatus;
    assetMetadataId: number;
    assetName: string;
    note: string;
    publicDownloadLink: string | null;
    resolution?: string;
    recordingType?: WebinarRecordingType;
}

interface IUpdateArtifactStatusPayload {
    assetMetadataId: string;
    status: ArtifactStatus;
    resolution?: string;
    recordingType?: WebinarRecordingType;
}

interface IUpdateArtifactSubscriptionPayload {
    value: {
        data: {
            onUpdateAssetMetadata: IUpdateArtifactStatusPayload;
        };
    };
}

interface IArtifactPublicLinkDTO {
    assetMetadataId: number;
    objectKey: string;
    publicDownloadLink: string;
    title: string;
}

enum RequestArtifactsTab {
    artifacts = 'artifacts',
    replays = 'replays'
}

type TRequestArtifactsTabForPayload = 'ARTIFACTS' | 'REPLAYS';

type TRequestArtifactsTab = keyof typeof RequestArtifactsTab;

interface IArtifactsParams extends IEventRequestParams {
    assetMetadataId: number;
}
interface IArtifactsPayload {
    meetingIds: number[];
    artifactTypes: ArtifactsTypeForBatchRequest[];
    resolution: string;
    recordingType: RecordingType;
}

interface IPassportReplaysPayload {
    meetingIds: number[];
    reviewRequired: boolean;
    placeholderText: string;
    recordingType: RecordingType;
}

interface IRequestArtifactsPayload {
    artifacts?: IArtifactsPayload;
    replays?: IPassportReplaysPayload;
}

interface IArtifactMp4Options {
    value: ResolutionMp4;
    label: string;
}

interface IArtifactMp4RecordingTypes {
    value: RecordingType;
    label: string;
    textInTable: string;
}

type TGetArtifactsSourcesSortKeys = 'startDateTime';

type TArtifactsSourcesFilterParams = IEventRequestParams & {
    type: TRequestArtifactsTabForPayload;
};

type TGetArtifactsSourcesRequestParams = TArtifactsSourcesFilterParams
    & (ISortFieldsRequestParams<TGetArtifactsSourcesSortKeys> | ISortRequestParams<TGetArtifactsSourcesSortKeys>)
;

enum WebinarRecordingType {
    SHARED_SCREEN_WITH_SPEAKER_VIEW_CC = 'SHARED_SCREEN_WITH_SPEAKER_VIEW_CC',
    SHARED_SCREEN_WITH_SPEAKER_VIEW = 'SHARED_SCREEN_WITH_SPEAKER_VIEW',
    SHARED_SCREEN_WITH_GALLERY_VIEW = 'SHARED_SCREEN_WITH_GALLERY_VIEW',
    GALLERY_VIEW = 'GALLERY_VIEW',
    SHARED_SCREEN = 'SHARED_SCREEN',
    AUDIO_ONLY = 'AUDIO_ONLY',
    AUDIO_TRANSCRIPT = 'AUDIO_TRANSCRIPT',
    CHAT_FILE = 'CHAT_FILE',
    ACTIVE_SPEAKER = 'ACTIVE_SPEAKER',
    HOST_VIDEO = 'HOST_VIDEO',
    AUDIO_ONLY_EACH_PARTICIPANT = 'AUDIO_ONLY_EACH_PARTICIPANT',
    CC_TRANSCRIPT = 'CC_TRANSCRIPT',
    CLOSED_CAPTION = 'CLOSED_CAPTION',
    POLL = 'POLL',
    TIMELINE = 'TIMELINE',
    THUMBNAIL = 'THUMBNAIL',
    AUDIO_INTERPRETATION = 'AUDIO_INTERPRETATION',
    SUMMARY = 'SUMMARY',
    SUMMARY_NEXT_STEPS = 'SUMMARY_NEXT_STEPS',
    SUMMARY_SMART_CHAPTERS = 'SUMMARY_SMART_CHAPTERS',
    SIGN_INTERPRETATION = 'SIGN_INTERPRETATION',
    SPEAKER_VIEW = 'SPEAKER_VIEW',
}

enum VideoResolution {
    RESOLUTION_360P = '360p',
    RESOLUTION_720P = '720p',
    RESOLUTION_1080P = '1080p',
}

export {
    IArtifactsDAO,
    IArtifactDTO,
    ArtifactStatus,
    IMeetingArtifactDTO,
    IMeetingsArtifactsListDTO,
    ArtifactType,
    IArtifactForDownload,
    IInitialArtifactsListDTO,
    IInitialArtifactDTO,
    IUpdateArtifactSubscriptionPayload,
    IUpdateArtifactStatusPayload,
    IArtifactPublicLinkDTO,
    ArtifactsTypeForBatchRequest,
    RequestArtifactsTab,
    TRequestArtifactsTab,
    IRequestArtifactsPayload,
    TRequestArtifactsTabForPayload,
    TGetArtifactsSourcesRequestParams,
    ResolutionMp4,
    IArtifactMp4Options,
    IRequestMeetingArtifactDTO,
    WebinarRecordingType,
    VideoResolution,
    ReplaysOptions,
    TReplaysOptionsForBatchRequest,
    IArtifactsParams,
    RecordingType,
    IArtifactMp4RecordingTypes,
};
